import { isArray, isNotEmpty, toJSON, unique } from '@rehaje/shared'

import { UnknownSchema } from '../../constants'
import { SchemaDefinition } from '../../models/SchemaDefinition'

import type { SchemaSetSpecific } from '../../types'

export class SchemaSetDefinition extends SchemaDefinition {
  override defaultsMerge(value: any, schema: SchemaSetSpecific) {
    return value ?? this.getDefaults(schema)
  }

  getDefaults(schema: SchemaSetSpecific): any | undefined {
    return new Function(
      `return new Set(${JSON.stringify(toJSON(schema.entries) ?? [])})`
    )()
  }

  override getIn(schema: SchemaSetSpecific, path: PropertyKey[]) {
    if (path.length === 0) return schema
    return UnknownSchema
  }

  override infer(
    target: any,
    schema: SchemaSetSpecific,
    path: PropertyKey[]
  ): SchemaSetSpecific {
    if (!(target instanceof Set) || !this.test(target, schema, path)) {
      return { ...schema, type: 'Schema.Set' }
    }
    return {
      ...schema,
      entries: Array.from(target.entries()),
      type: 'Schema.Set'
    }
  }

  override inferOnlySchema(schema: SchemaSetSpecific) {
    return schema
  }

  override inferTypeScript(
    schema: SchemaSetSpecific,
    path: PropertyKey[]
  ): string {
    const { entries } = schema
    if (isArray(entries)) {
      const valueTypes = unique(
        entries.map((entry, index) =>
          super.inferTypeScript(
            this.inferShapeSchema(entry),
            path.concat(index)
          )
        )
      )
      return `Set<${valueTypes.length > 0 ? valueTypes.join(' | ') : 'any'}>`
    }
    return `Set<any>`
  }

  override prune(target: any): any {
    return target
  }

  override transform(
    target: any,
    schema: SchemaSetSpecific,
    path: PropertyKey[]
  ) {
    if (target === null && schema.nullable) return target
    if (!this.test(target, schema, path)) return this.getDefaults(schema)
    return target
  }

  override validate(target: any, schema: SchemaSetSpecific): void {
    this.validateMetadata(
      target,
      schema,
      (value) => value instanceof Set,
      isNotEmpty
    )
  }
}
